import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static targets = [
    'listView',
    'graphView',
    'mapView',
    'listTab',
    'graphTab',
    'mapTab',
    'sidebar',
    'sidebarTitle',
    'sidebarContent',
    'filterBtn',
    'searchInput',
  ];

  static values = {
    orgUrl: String,
    editUrl: String,
    destroyUrl: String,
    listUrl: String,
    initialView: { type: String, default: 'list' },
  };

  connect() {
    this.filters = {
      state: this.getUrlParam('state') || '',
      segment: this.getUrlParam('segment') || '',
      discipline: this.getUrlParam('discipline') || '',
      q: this.getUrlParam('q') || '',
      tier: this.getUrlParam('tier') || '',
      sort: this.getUrlParam('sort') || '',
      direction: this.getUrlParam('direction') || '',
    };
    this.currentPage = this.getUrlParam('page') || '';
    this.graphMode = this.getUrlParam('graph_mode') || 'segment';

    this.currentView = this.getUrlParam('view') || this.initialViewValue;
    this.selectedOrgId = this.getUrlParam('org') || null;
    this.setActiveView(this.currentView);
    this.initializeTooltips(this.element);

    // Listen for org click events from graph and map views
    this.graphOrgClickHandler = (event) => this.loadOrganizationDetail(event.detail.orgId);
    this.mapOrgClickHandler = (event) => this.loadOrganizationDetail(event.detail.orgId);
    this.element.addEventListener('icp-universe-graph:org-clicked', this.graphOrgClickHandler);
    this.element.addEventListener('icp-universe-map:org-clicked', this.mapOrgClickHandler);

    // Restore selected org from URL on page load
    if (this.selectedOrgId) {
      this.loadOrganizationDetail(this.selectedOrgId);
    }
  }

  // --- View Switching ---

  switchView(event) {
    const { view } = event.params;
    this.setActiveView(view);
    this.updateUrl();
    this.applyFiltersToCurrentView();
  }

  setActiveView(view) {
    this.currentView = view;

    this.listViewTarget.classList.toggle('d-none', view !== 'list');
    this.graphViewTarget.classList.toggle('d-none', view !== 'graph');
    this.mapViewTarget.classList.toggle('d-none', view !== 'map');

    this.listTabTarget.classList.toggle('btn-primary', view === 'list');
    this.listTabTarget.classList.toggle('btn-secondary', view !== 'list');
    this.graphTabTarget.classList.toggle('btn-primary', view === 'graph');
    this.graphTabTarget.classList.toggle('btn-secondary', view !== 'graph');
    this.mapTabTarget.classList.toggle('btn-primary', view === 'map');
    this.mapTabTarget.classList.toggle('btn-secondary', view !== 'map');
  }

  // --- Filtering ---

  applyFilter(event) {
    const key = event.currentTarget.dataset.filterKey;
    const value = event.currentTarget.dataset.filterValue || '';

    // If clicking the "All" button (empty value), clear the filter
    // If clicking the already-active value, clear it (toggle off)
    // Otherwise, set the new value
    if (value === '' || this.filters[key] === value) {
      this.filters[key] = '';
    } else {
      this.filters[key] = value;
    }

    this.currentPage = ''; // Reset to page 1 on filter change
    this.updateUrl();
    this.updateFilterUI();
    this.applyFiltersToCurrentView();
  }

  applySearch() {
    if (this.hasSearchInputTarget) {
      this.filters.q = this.searchInputTarget.value.trim();
    }
    this.currentPage = ''; // Reset to page 1 on search
    this.updateUrl();
    this.applyFiltersToCurrentView();
  }

  applyFiltersToCurrentView() {
    // Dispatch filter event for graph/map controllers
    const event = new CustomEvent('icp-universe:filters-changed', {
      bubbles: true,
      detail: { filters: { ...this.filters } },
    });
    this.element.dispatchEvent(event);

    // Fetch updated list
    if (this.currentView === 'list') {
      this.fetchFilteredList();
    }
  }

  async fetchFilteredList() {
    const params = new URLSearchParams();
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    if (this.currentPage) params.set('page', this.currentPage);

    const url = `${this.listUrlValue}?${params.toString()}`;

    try {
      this.listViewTarget.style.opacity = '0.5';
      const response = await fetch(url);
      if (response.ok) {
        const html = await response.text();
        this.listViewTarget.innerHTML = html;
        this.interceptPaginationLinks();
        this.initializeTooltips(this.listViewTarget);
      }
    } catch (error) {
      console.error('Failed to fetch filtered list:', error);
    } finally {
      this.listViewTarget.style.opacity = '1';
    }
  }

  interceptPaginationLinks() {
    this.listViewTarget.querySelectorAll('.pagination a[href]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(link.href, window.location.origin);
        this.currentPage = url.searchParams.get('page') || '';
        this.updateUrl();
        this.fetchFilteredList();
      });
    });

    // Also intercept sort header links
    this.listViewTarget.querySelectorAll('.icp-universe-table th a[href]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const url = new URL(link.href, window.location.origin);
        this.filters.sort = url.searchParams.get('sort') || '';
        this.filters.direction = url.searchParams.get('direction') || '';
        this.currentPage = ''; // Reset to page 1 on sort change
        this.updateUrl();
        this.fetchFilteredList();
      });
    });
  }

  // --- URL State ---

  updateUrl() {
    const params = new URLSearchParams();
    if (this.currentView !== 'list') params.set('view', this.currentView);
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    if (this.currentPage && this.currentPage !== '1') params.set('page', this.currentPage);
    if (this.selectedOrgId) params.set('org', this.selectedOrgId);

    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
    window.history.replaceState(window.history.state, '', newUrl);
  }

  getUrlParam(key) {
    return new URLSearchParams(window.location.search).get(key) || '';
  }

  // --- Filter UI ---

  updateFilterUI() {
    this.filterBtnTargets.forEach((btn) => {
      const key = btn.dataset.filterKey;
      const value = btn.dataset.filterValue || '';
      const isActive = this.filters[key] === value;

      // Handle badge-style buttons (state chips)
      if (btn.classList.contains('badge')) {
        btn.classList.toggle('badge-primary', isActive);
        btn.classList.toggle('badge-outline-secondary', !isActive);
        btn.classList.toggle('border', !isActive);
      } else {
        // Handle btn-group buttons (segment, discipline)
        btn.classList.toggle('btn-primary', isActive);
        btn.classList.toggle('btn-secondary', !isActive);
      }
    });
  }

  // --- Sidebar ---

  async showOrganization(event) {
    const { orgId } = event.currentTarget.dataset;
    if (!orgId) return;

    this.element.querySelectorAll('.icp-universe-row.active').forEach((el) => el.classList.remove('active'));
    event.currentTarget.classList.add('active');

    this.loadOrganizationDetail(orgId);
  }

  closeSidebar() {
    this.sidebarTarget.classList.add('d-none');
    this.element.querySelectorAll('.icp-universe-row.active').forEach((el) => el.classList.remove('active'));
    this.selectedOrgId = null;
    this.updateUrl();
    document.dispatchEvent(new CustomEvent('icp-universe:sidebar-closed'));
  }

  // --- Edit modal ---

  async openEditModal(event) {
    const orgId = event.params.orgId || this.selectedOrgId;
    if (!orgId) return;

    const modalContent = document.querySelector('#editOrgModal .modal-content');
    if (modalContent) {
      modalContent.innerHTML =
        '<div class="p-4 text-center"><div class="spinner-border text-primary" role="status"></div></div>';
    }

    if (window.$) {
      window.$('#editOrgModal').modal('show');
    }

    try {
      const response = await fetch(this.editUrlValue.replace(':id', orgId), {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('Failed to load edit form');
      const html = await response.text();
      if (!modalContent) return;
      modalContent.innerHTML = html;
      const form = modalContent.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => this.saveOrg(e));
      }
    } catch {
      if (modalContent) {
        modalContent.innerHTML = '<div class="p-4 text-danger">Failed to load edit form.</div>';
      }
    }
  }

  async saveOrg(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const errorBox = form.querySelector('[data-icp-universe-target="editErrors"]');
    if (errorBox) {
      errorBox.classList.add('d-none');
      errorBox.textContent = '';
    }

    try {
      const response = await fetch(form.action, {
        method: form.method?.toUpperCase() === 'GET' ? 'PATCH' : form.method,
        headers: { 'X-CSRF-Token': token, Accept: 'text/html, application/json' },
        credentials: 'same-origin',
        body: new FormData(form),
      });

      if (response.ok) {
        const html = await response.text();
        if (window.$) {
          window.$('#editOrgModal').modal('hide');
        }
        this.sidebarContentTarget.innerHTML = html;
        this.initializeTooltips(this.sidebarContentTarget);
        this.fetchFilteredList();
      } else {
        const payload = await response.json().catch(() => ({}));
        const messages = Array.isArray(payload.errors) ? payload.errors.join(', ') : 'Save failed.';
        if (errorBox) {
          errorBox.textContent = messages;
          errorBox.classList.remove('d-none');
        } else {
          window.alert(messages);
        }
      }
    } catch {
      window.alert('Save failed. Please try again.');
    }
  }

  async deleteOrg(event) {
    const { orgId } = event.params;
    const orgName = event.params.orgName || 'this organization';
    if (!orgId) return;

    if (!window.confirm(`Delete ${orgName}? This will hide it from the ICP Universe.`)) return;

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    try {
      const response = await fetch(this.destroyUrlValue.replace(':id', orgId), {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': token, Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (response.ok) {
        this.closeSidebar();
        this.fetchFilteredList();
      } else {
        window.alert('Delete failed. Please try again.');
      }
    } catch {
      window.alert('Delete failed. Please try again.');
    }
  }

  async toggleReachedOut(event) {
    const url = event.currentTarget.dataset.icpUniverseUrlParam;
    if (!url) return;

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'X-CSRF-Token': token, Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (response.ok) {
        const html = await response.text();
        this.sidebarContentTarget.innerHTML = html;
        this.initializeTooltips(this.sidebarContentTarget);
      }
    } catch (error) {
      console.error('Failed to toggle reached-out:', error);
    }
  }

  initializeTooltips(root = document) {
    if (window.$ && window.$.fn.tooltip) {
      window.$('[data-toggle="tooltip"]', root).tooltip();
    }
  }

  async loadOrganizationDetail(orgId) {
    if (!orgId) return;

    this.selectedOrgId = orgId;
    this.updateUrl();

    // Notify graph/map controllers of the selection
    document.dispatchEvent(
      new CustomEvent('icp-universe:org-selected', {
        bubbles: true,
        detail: { orgId },
      })
    );

    this.sidebarTarget.classList.remove('d-none');
    this.sidebarContentTarget.innerHTML =
      '<div class="p-3 text-center"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>';

    try {
      const response = await fetch(this.orgUrlValue.replace(':id', orgId));
      if (response.ok) {
        const html = await response.text();
        this.sidebarContentTarget.innerHTML = html;
        this.sidebarTitleTarget.textContent = 'Organization Details';
        this.initializeTooltips(this.sidebarContentTarget);
      }
    } catch (error) {
      this.sidebarContentTarget.innerHTML = '<p class="text-danger p-3">Failed to load details.</p>';
    }
  }

  switchGraphMode(event) {
    const { graphMode: mode } = event.currentTarget.dataset;
    if (!mode) return;

    this.graphMode = mode;
    this.element.querySelectorAll('[data-graph-mode-btn]').forEach((btn) => {
      const active = btn.dataset.graphMode === mode;
      btn.classList.toggle('btn-primary', active);
      btn.classList.toggle('btn-secondary', !active);
    });

    const customEvent = new CustomEvent('icp-universe:graph-mode-changed', {
      bubbles: true,
      detail: { mode },
    });
    this.element.dispatchEvent(customEvent);
  }

  disconnect() {
    this.element.removeEventListener('icp-universe-graph:org-clicked', this.graphOrgClickHandler);
    this.element.removeEventListener('icp-universe-map:org-clicked', this.mapOrgClickHandler);
  }
}
